import libphonenumber, { PhoneNumberFormat } from "google-libphonenumber";
import jwtDecode from "jwt-decode";
import { httpsCallable } from "firebase/functions";
import { functionsInstance } from "firebase-setup";
import _, { isEmpty } from "lodash";
import { Typography } from "@mui/material";
import { marked } from "marked";
import axios from "axios";
import { HTTPAidaBaseService } from "adapters/http";
import dayjs from "dayjs";

// We use an ephemeral, in-memory chat history for this demo.
import { InMemoryChatMessageHistory } from "@langchain/core/chat_history";
import { ChatPromptTemplate } from "@langchain/core/prompts";
import { RunnableWithMessageHistory } from "@langchain/core/runnables";
import i18n from "multilingual";
import utc from "dayjs/plugin/utc"; // Import the UTC plugin
import duration from "dayjs/plugin/duration";
// import { getDescription } from "cron-descriptor";
import cronstrue from "cronstrue";
import { FacebookOutlined } from "@mui/icons-material";

const messageHistories = {};

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

// Function to format a phone number based on country
export const formatPhoneNumber = (phoneNumber, countryCode = "US") => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode);
    return phoneUtil.format(
      number,
      libphonenumber.PhoneNumberFormat.INTERNATIONAL
    );
  } catch (error) {
    console.error("Invalid number:", error);
    return null;
  }
};

export const extractFirstTwoParamsFromUrl = (url) => {
  const matches = url.match(/^\/([^/]+)\/([^/]+)/);
  if (matches && matches.length === 3) {
    return `/${matches[1]}/${matches[2]}`;
  } else {
    return url;
  }
};

export const getTenantFromToken = () => {
  try {
    let token = localStorage?.getItem("jwtToken");
    if (token) {
      token = jwtDecode(token);
      return token?.firebase?.tenant || "";
    } else return "";
  } catch (e) {
    console.error(e);
  }
};

function mergeObjectsByRole(objects) {
  const merged = [];

  objects.forEach((obj) => {
    if (merged.length === 0) {
      // If the merged array is empty, add the first object
      merged.push({ ...obj });
    } else {
      const lastObject = merged[merged.length - 1];

      if (lastObject.role === obj.role) {
        // If the current object has the same role as the last object in the result array,
        // merge them. Adjust this logic depending on what "merge" means for your data.
        // Here, it's simply combining other fields assuming they're not arrays or objects.
        for (const key in obj) {
          if (key === "parts") {
            if (lastObject.hasOwnProperty(key)) {
              let text = lastObject[key]?.[0]?.text;
              text += obj[key]?.[0]?.text; // Assuming you can sum the properties
              lastObject[key] = [{ text }];
            } else {
              lastObject[key] = obj[key];
            }
          }
        }
      } else {
        // If roles are different, simply add the current object to the results
        merged.push({ ...obj });
      }
    }
  });

  return merged;
}

export const generateResponseLanchainHelper = async (
  message,
  { backstory = "", sessionId = "jenny_chat_history", chatModel = "" } = {}
) => {
  if (!chatModel) {
    console.error("No chat model provided");
  }

  const prompt = ChatPromptTemplate.fromMessages([
    ["system", `${backstory}`],
    ["placeholder", "{chat_history}"],
    ["human", "{input}"],
  ]);

  const chain = prompt.pipe(chatModel);

  const withMessageHistory = new RunnableWithMessageHistory({
    runnable: chain,

    getMessageHistory: async (sessionId) => {
      if (messageHistories[sessionId] === undefined) {
        messageHistories[sessionId] = new InMemoryChatMessageHistory();
      }
      return messageHistories[sessionId];
    },

    inputMessagesKey: "input",
    historyMessagesKey: "chat_history",
  });

  const config = {
    configurable: {
      sessionId: sessionId,
    },
  };

  let response = null;

  try {
    response = await withMessageHistory.invoke(
      {
        input: message,
      },
      config
    );
  } catch (e) {
    console.error(e);
  }

  return {
    returnText: response?.content,
  };
};

export const generateResponseForPrompt = async (
  promptText,
  { contextList = [], max_gen_len = 8000 } = {}
) => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const controller = new AbortController();

  const token = localStorage.getItem(`jwtToken`);
  if (!token) throw Error("No token found");

  const jwtPayload = jwtDecode(token);
  let tenantId = jwtPayload?.firebase?.tenant;

  contextList.push({ role: "user", text: promptText });

  let tempList = mergeObjectsByRole(
    contextList?.map((item) => ({
      role: item?.role,
      parts: [{ text: item?.text }],
    }))
  );

  console.log("finalContextList", tempList);

  const bodyOfRequest = {
    contents: tempList,
    safety_settings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
    ],
    generation_config: {
      temperature: 0,
      topP: 1,
      candidateCount: 1,
      maxOutputTokens: max_gen_len,
    },
  };
  let returnText = "";

  const response = await HTTPAidaBaseService.post(
    `/${tenantId}/submit-prompt`,
    { data: bodyOfRequest },
    { cancelToken: source.token, signal: controller.signal }
  );

  returnText = response?.data?.candidates?.[0]?.content?.parts?.[0]?.text;

  return { returnText, source, controller };
};

export const generateResponseFromGemini = async (
  promptText,
  { contextList = [], max_gen_len = 500 } = {}
) => {
  let tempList = contextList?.map((item) => ({
    role: item?.role,
    parts: { text: item?.text },
  }));

  tempList.push({ role: "user", parts: { text: promptText } });

  const bodyOfRequest = {
    contents: tempList,
    safety_settings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
    ],
    generation_config: {
      temperature: 0.2,
      topP: 0.2,
      candidateCount: 1,
      maxOutputTokens: max_gen_len,
    },
  };
  let returnText = "";

  const processDocument = httpsCallable(functionsInstance, "modelPredict");

  const documentData = {
    ...bodyOfRequest,
  };

  const response = await processDocument(documentData);

  response?.data?.forEach((item) => {
    const temp1 = item?.candidates?.[0]?.content;
    if (temp1?.role === "model") {
      returnText += temp1?.parts?.[0]?.text;
    }
  });

  return { returnText };
};

export const fixAndParseJsonFromGPT = (
  jsonString,
  { customPattern = null } = {}
) => {
  const objectRegex = customPattern
    ? customPattern
    : /\{(?:[^{}]|(?:\{(?:[^{}]|{\})*\}))*\}/g;
  let match;
  const objectsArrays = [];

  while ((match = objectRegex.exec(jsonString)) !== null) {
    try {
      let obj = match[0];
      // console.log("obj before", obj);
      // obj = obj?.replace(/(\r\n|\n|\r)/gm, "");

      // obj = obj.replace(/\/\/.*|\/\*[^]*?\*\//g, "");

      // obj = obj.replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2": ');

      // console.log("obj after", obj);

      if (typeof obj === "string" && obj.length > 0) {
        const repaired = JSON.parse(obj);
        objectsArrays.push(repaired);
      }

      // if (typeof obj === "object") {
      //   objectsArrays.push(obj);
      // }
    } catch (e) {
      console.error("Invalid JSON object:", match[0]);
    }
  }
  return objectsArrays;
};
export const getFhirSkeleton = (fhirResource, {} = {}) => {
  let resourceToReturn = {};
  if (fhirResource === "FHIR_CLINICAL_IMPRESSION") {
    resourceToReturn = {
      code: {
        coding: [
          {
            code: "51848-0",
            display: "Encounter specific assessment note",
            system: "https://loinc.org",
          },
        ],
        text: "Encounter specific assessment note",
      },
      description: "",
      effectivePeriod: {
        start: "",
      },
      encounter: {
        reference: "",
      },
      resourceType: "ClinicalImpression",
      status: "in-progress",
      subject: {
        reference: "",
      },
    };
  } else if (fhirResource === "FHIR_ENCOUNTER") {
    resourceToReturn = {
      class: {
        code: "308720009",
        display: "Untitled",
      },
      period: {
        start: "",
      },
      reasonCode: [
        {
          coding: [
            {
              display: "",
            },
          ],
          text: "",
        },
      ],
      resourceType: "Encounter",
      status: "in-progress",
      subject: {
        reference: "",
      },
      type: [
        {
          coding: [
            {
              code: "308720009",
              display: "Letter",
            },
          ],
        },
      ],
    };
  } else if (fhirResource === "FHIR_COMPOSITION") {
    resourceToReturn = {
      date: new Date().toISOString(),
      title: "",
      status: "preliminary",
      type: {
        coding: [
          {
            code: "00000-0",
            display: "gen-transcript",
            system: "https://loinc.org",
          },
        ],
      },
      subject: {
        reference: "",
      },
      encounter: {
        reference: "",
      },
      author: [
        {
          reference: "Practitioner/example",
        },
      ],
      section: [
        {
          title: "",
          text: {
            status: "generated",
            div: "",
          },
        },
      ],
      resourceType: "Composition",
    };
  }

  return resourceToReturn;
};

export const formatClinicalNoteAndRepair = (note) => {
  try {
  } catch (e) {
    console.error(e);
  }

  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let formattedWithMarked = marked?.parse(note);

  // Add bootstrap classes to table
  formattedWithMarked = formattedWithMarked?.replace(
    "<table>",
    `<table class='table table-bordered table-striped table-sm'>`
  );

  // Make all links open in new tab
  formattedWithMarked = formattedWithMarked?.replace(
    /<a\s+(?![^>]*target=)/g,
    '<a target="_blank" rel="noopener noreferrer" '
  );

  return formattedWithMarked;
};

export const formatClinicalNote = (note) => {
  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let formattedWithMarked = marked?.parse(note);
  formattedWithMarked = formattedWithMarked?.replace(
    "<table>",
    `<table class='table'>`
  );
  return formattedWithMarked;
};

export const formatSoapNote = (note) => {
  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let lines = note.split("\n");

  const tableData = lines?.filter((line) => line.includes("|"));

  lines = lines.filter((line) => !line.includes("|"));

  const formattedLines = lines?.map((line) => {
    const boldRegex = /\*\*(.*?)\*\*/g;
    const boldMatches = line.matchAll(boldRegex);
    let formattedLine = line;
    for (const match of boldMatches) {
      formattedLine = formattedLine.replace(match[0], `<b>${match[1]}</b>`);
    }
    if (formattedLine.startsWith("*") && formattedLine?.length > 2) {
      formattedLine = `- ${formattedLine.substring(1).trim()}`;
    }
    return formattedLine.replace(/\*$/g, "");
  });

  if (tableData?.length > 0) {
    formattedLines?.push(table(tableData.join("\n")));
  }
  return formattedLines.join("<br/>");
};

const table = (dataString) => {
  // Split the string into rows
  let rows = dataString.split("\n").map((row) => row.trim());

  let elements = rows?.join("").split("|");

  let tempRows = [];
  let currentRow = [];

  elements.forEach((element) => {
    if (element === "") {
      if (currentRow.length > 0) {
        tempRows.push(currentRow.join("|"));
        currentRow = [];
      }
    } else {
      currentRow.push(element.trim());
    }
  });

  // Push the last row if it's not empty
  if (currentRow.length > 0) {
    tempRows.push(currentRow.join("|"));
  }

  rows = tempRows;

  const tableHtml = `
    <table class='table'>
      <thead>
        <tr>
          ${rows[0]
            .split("|")
            .map((cell) => `<th>${cell.trim()}</th>`)
            .join("")}
        </tr>
      </thead>
      <tbody>
        ${rows
          .slice(2)
          .map(
            (row) => `
          <tr>
            ${row
              .split("|")
              .map((cell) => `<td>${cell.trim()}</td>`)
              .join("")}
          </tr>
        `
          )
          .join("")}
      </tbody>
    </table>
  `;

  return tableHtml;
};

export const genderOptionList = [
  {
    label: "GENDER.MALE",
    value: "male",
  },
  {
    label: "GENDER.FEMALE",
    value: "female",
  },
  {
    label: "GENDER.OTHER",
    value: "other",
  },
  {
    label: "GENDER.UNKNOWN",
    value: "unknown",
  },
];

export const FormError = ({ error, sx }) => {
  return (
    error && (
      <Typography
        variant="caption"
        component="p"
        sx={{ color: "#df2416", fontWeight: 600, mt: 1, ...sx }}
      >
        {error}
      </Typography>
    )
  );
};

export const discoverCreatedTime = (CreatedDate) => {
  const createdDate = dayjs(CreatedDate);
  const currentDate = dayjs();

  const diffInSeconds = currentDate.diff(createdDate, "second");
  const diffInMinutes = currentDate.diff(createdDate, "minute");
  const diffInHours = currentDate.diff(createdDate, "hour");
  const diffInDays = currentDate.diff(createdDate, "day");

  let timeAgo;
  if (diffInDays < 1) {
    if (diffInHours < 1) {
      if (diffInMinutes < 1) {
        timeAgo = `${diffInSeconds} ${
          diffInSeconds == 1 ? "second" : "seconds"
        } ago`;
        // timeAgo = `Created within an hour`;
      } else {
        timeAgo = `${diffInMinutes} ${
          diffInMinutes == 1 ? "minute" : "minutes"
        } ago`;
        // timeAgo = `Created within an hour`;
      }
    } else {
      timeAgo = `${diffInHours} ${diffInHours == 1 ? "hour" : "hours"} ago`;
    }
  } else {
    timeAgo = `${diffInDays} ${diffInDays == 1 ? "day" : "days"} ago`;
  }

  return timeAgo;
};

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const cacheTenantIds = (data) => {
  if (!localStorage.getItem("tenants")) {
    localStorage.setItem("tenants", JSON.stringify([]));
  }
  let tenantsList = localStorage.getItem("tenants");
  let newTenantObj = {
    tenant: data?.tenantId,
    email: data?.email,
  };
  if (isJsonString(tenantsList)) {
    tenantsList = JSON.parse(tenantsList);
    if (tenantsList.filter((f) => f?.email == data?.email).length < 1) {
      tenantsList.push(newTenantObj);
      localStorage.setItem("tenants", JSON.stringify(tenantsList));
    }
  } else {
    localStorage.setItem("tenants", JSON.stringify([newTenantObj]));
  }
};

export const getCachedTenant = (email) => {
  if (!localStorage.getItem("tenants")) {
    localStorage.setItem("tenants", JSON.stringify([]));
  }
  let tenantsList = localStorage.getItem("tenants");
  if (isJsonString(tenantsList)) {
    tenantsList = JSON.parse(tenantsList);
    let tenantData = tenantsList.filter((f) => f?.email == email);
    if (tenantData.length) {
      return tenantData[0]?.tenant;
    } else return false;
  } else return false;
};

export const parseClinicalNote = (clinicalNote) => {
  try {
    // Remove leading and trailing ``` markers
    let cleanedNote = clinicalNote
      .replace(/^(?:```json|```JSON|```)/, "") // Remove leading ```json or ```JSON or ```
      .replace(/```$/, ""); // Remove trailing ```

    // Ensure keys are enclosed in double quotes
    cleanedNote = cleanedNote.replace(
      /([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g,
      '$1"$2"$3'
    );

    // Parse the cleaned JSON string
    return JSON.parse(cleanedNote);
  } catch (error) {
    console.error("Failed to parse clinical note:", error);
    return null; // or handle error as needed
  }
};

export const findOldestObject = (nestedArray) => {
  if (!Array.isArray(nestedArray)) {
    throw new TypeError("The input should be an array of arrays");
  }

  // Flatten the nested array
  const flattenedArray = nestedArray.flat();

  // Use reduce to find the oldest object
  const oldestObject = flattenedArray.reduce((oldest, current) => {
    if (current && current.resource && current.resource.date) {
      const currentDate = new Date(current.resource.date);
      if (!oldest || currentDate < new Date(oldest.resource.date)) {
        return current;
      }
    }
    return oldest;
  }, null);

  return oldestObject;
};

export function getMostRecentObject(objects) {
  if (!objects?.length) return null;

  return objects?.reduce((mostRecent, current) => {
    const mostRecentDate = new Date(mostRecent?.resource?.date);
    const currentDate = new Date(current?.resource?.date);
    return currentDate > mostRecentDate ? current : mostRecent;
  });
}

export const cleanName = (name) => {
  if (!name) {
    return false;
  }
  // Replace any comma followed by optional whitespace with a single space
  return name.replace(/,\s*/g, " ");
};

export function replaceNulls(obj) {
  try {
    if (
      [null, undefined, "null", "undefined", "Null", "NULL"]?.includes(obj) ||
      isEmpty(obj)
    ) {
      return "";
    } else if (Array.isArray(obj)) {
      if (
        obj.length === 0 ||
        obj.every((item) =>
          [null, "null", undefined, "", "Null", "NULL"]?.includes(item)
        )
      ) {
        return [];
      }
      if (obj?.length > 0) {
        obj = obj?.filter(
          (f) => ![null, "null", undefined, "", "Null", "NULL"]?.includes(f)
        );
      }
      return obj.map(replaceNulls);
    } else if (typeof obj === "object") {
      const newObj = {};
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          newObj[key] = replaceNulls(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  } catch (e) {
    console.error(e);
  }
}

export const getCurrentRegenValue = (arr, selectedOption1) => {
  let selectedOption = JSON.parse(JSON.stringify(selectedOption1));
  let g;
  let regenerateRawObject;

  const isTranslateSOAPNote =
    selectedOption?.label === "Translate SOAP Note to Spanish" ||
    selectedOption?.label === "Translate SOAP Note to English";

  if (isTranslateSOAPNote) {
    selectedOption.prompt_name = arr["SOAP Note for First Visit"]
      ? "SOAP Note for First Visit"
      : "SOAP Note for Follow-up Visit";
    regenerateRawObject = arr?.[selectedOption?.prompt_name]?.component?.find(
      (element) => {
        const code = element?.code?.coding?.[0]?.code;
        return code === "translate-soap";
      }
    );
    try {
      g = Number(regenerateRawObject?.valueInteger);
    } catch (e) {
      console.error(e);
    }
  } else {
    regenerateRawObject = arr?.[selectedOption?.prompt_name]?.component?.find(
      (element) => {
        const code = element?.code?.coding?.[0]?.code;
        return code !== "translate-soap";
      }
    );
    try {
      g = Number(regenerateRawObject?.valueInteger);
    } catch (e) {
      console.error(e);
    }
  }

  return g || 0;
};

export const exceptionalRoutes = [
  "verify",
  "business-associate-agreement",
  "reset-password",
  "login",
  "signup",
];

export const mainTheme = "#DD3F59";
export const secondaryTheme = "#fff";
export const secondaryThemeDark = "#212121";
export const transparent = "transparent";
export const maroon = "#db3957";

export const getRelativeTime = (key) => {
  return ["Today", "Yesterday"]?.includes(key)
    ? i18n.t(`common.${key?.toUpperCase()}`)
    : key;
};

dayjs.extend(utc);
dayjs.extend(duration);

export function getRemainingTime(createdAt) {
  createdAt = parseInt(createdAt);

  // Convert createdAt to a Day.js UTC object
  const createdAtUTC = dayjs.utc(createdAt);
  // Calculate the expiration date by adding 30 days
  const expirationDate = createdAtUTC.add(30, "day");

  // Get the current time in UTC
  const currentTime = dayjs.utc();

  // Calculate the difference
  const remainingTime = expirationDate.diff(currentTime);

  // If the account has expired
  if (remainingTime < 0) {
    return "Account expired";
  }

  // Get duration from remaining time in milliseconds
  const duration = dayjs.duration(remainingTime);

  // Format the remaining time
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  // Create the remaining time string
  const parts = [];
  if (days > 0)
    parts.push(
      `${days} ${days > 1 ? i18n?.t("common.DAYS") : i18n?.t("common.DAY")}`
    );
  if (hours > 0)
    parts.push(
      `${hours} ${hours > 1 ? i18n?.t("common.HOURS") : i18n?.t("common.HOUR")}`
    );
  if (minutes > 0)
    parts.push(
      `${minutes} ${
        minutes > 1 ? i18n?.t("common.MINUTES") : i18n?.t("common.MINUTE")
      }`
    );
  // if (days > 0) parts.push(`${days}D`);
  // if (hours > 0) parts.push(`${hours}H`);
  // if (minutes > 0) parts.push(`${minutes}M`);

  return parts.length
    ? parts.join(" ")
    : i18n?.t("common.LESS_THAN_A_MIN_REMAINING");
}

export const mappedLanguages = {
  en: {
    languageCode: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "hh:mm A", // 12-hour format with AM/PM
    timeWithDateFormat: "MM/DD/YYYY hh:mm A", // Date + Time (12-hour format)
  },
  es: {
    languageCode: "es",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD/MM/YYYY HH:mm", // Date + Time (24-hour format)
  },
  "fr-FR": {
    languageCode: "fr",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD/MM/YYYY HH:mm", // Date + Time (24-hour format)
  },
  "pl-PL": {
    languageCode: "pl",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD.MM.YYYY HH:mm", // Date + Time (24-hour format)
  },
};

export const shouldRenderSearchField = (path) => {
  const excludePaths = [
    "/chat/window/",
    "/survey/happiness_survey",
    "/survey/happiness_form/",
    "/calllogs/list",
    // '/trials/list',
    "/store/plans",
    "/calllogs/log_details/",
    "/dashboard",
    "/trials/patient",
    "/completed/log_details",
    "/completed/list",
    "/sites/site",
    "onboarding/list",
    "/sites/list",
    "/sites/outreach",
    "/sites/onboarded",
    "/settings",
    "/studies",
    "/subjects",
    "/campaigns",
    "/mia",
  ];
  // Looping through each path in the excludePaths array
  return !excludePaths?.some((excludedPath) => path?.includes(excludedPath));
};

export const formatToE164 = (phoneNumber) => {
  try {
    const parsedNumber = phoneUtil.parse(phoneNumber); // works if input has '+' prefix
    if (phoneUtil.isValidNumber(parsedNumber)) {
      return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
    }
    return null;
  } catch {
    return null;
  }
};

export const formatToE164Smart = (phone) => {
  try {
    // First trying with US
    let parsed = phoneUtil.parse(phone, "US");
    if (phoneUtil.isValidNumber(parsed)) {
      return phoneUtil.format(parsed, PhoneNumberFormat.E164);
    }
  } catch {}

  try {
    // Then trying with IN
    let parsed = phoneUtil.parse(phone, "IN");
    if (phoneUtil.isValidNumber(parsed)) {
      return phoneUtil.format(parsed, PhoneNumberFormat.E164);
    }
  } catch {}

  return null;
};

export const formStatuses = [
  { label: "Completed", value: "Completed" },
  // { label: "Call Connected", value: "Call connected" },
  {
    label: "Not Qualified",
    value: "Not qualified",
  },
  {
    label: "Incomplete",
    value: "Incomplete",
  },
  {
    label: "Not Answered",
    value: "Customer did not answer",
  },
  // {
  //   label: "Busy",
  //   value: "Busy",
  // },
  // { label: "Declined/Missed", value: "Declined/Missed" },
  { label: "Sent", value: "Form assigned" },
  { label: "SMS response", value: "SMS response" },
  { label: "Appointment Scheduled", value: "Appointment Scheduled" },
  // { label: "None", value: "None" },
];

// Helper to parse and format label for each schedule plan entry
export const getSchedulePlanLabel = (section) => {
  try {
    // Parse the section.title JSON
    const info = JSON.parse(section.title);
    if (info.type === "recurring") {
      // Use cron-descriptor for human readable cron
      const cronText = info.expression
        ? // ? getDescription(info.expression, { use24HourTimeFormat: true })
          cronstrue.toString(info.expression, { verbose: true })
        : "";
      return `Recurring: ${cronText} (${info.startDate}, ${info.timezone}, ${info.duration?.value} ${info.duration?.unit})`;
    } else if (info.type === "fixed") {
      // Show start date, start time, timezone, duration
      return `Fixed: ${info.startDate} ${info.startTime || ""}${
        info.endTime ? ` - ${info.endTime}` : ""
      } (${info.timezone}, ${info.duration?.value} ${info.duration?.unit})`;
    }
    // fallback
    return section.title;
  } catch (e) {
    return section.title;
  }
};

/* place in a global stylesheet */
export const agBodyViewportStyle = {
  "& .ag-body-vertical-scroll": { display: "none !important" },
  "& .ag-body-viewport": { width: "100% !important" },
  "& .ag-center-cols-viewport, & .ag-center-cols-clipper": { right: 0 },
};

export const sourceLabels = {
  facebook_ad_campaign: {
    label: "Facebook Ad Campaign",
    icon: <FacebookOutlined style={{ color: "#1877F2" }} />,
  },

  "patient-source:facebook_ad_campaign": {
    label: "Facebook Ad Campaign",
    icon: <FacebookOutlined style={{ color: "#1877F2" }} />,
  },

  regular: {
    label: "Regular",
    icon: <></>,
  },
};

export const appointmentStatuses = {
  booked: "Booked",
  "checked-in": "Checked-In",
  cancelled: "Cancelled",
  fulfilled: "Completed",
};

// Location timezone helpers live in their own dependency-free module so they can
// be unit-tested in isolation (common_helper pulls in axios/firebase/langchain).
// Re-exported here to preserve existing `config/helpers/common_helper` imports.
export {
  TZ_CODE_EXT,
  LEGACY_LOCATION_TIMEZONE_EXT,
  getLocationTimezone,
} from "./location-timezone";
